import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { HxBaseControl } from '../../../base/hx-base-control';
import { ButtonContext, ButtonSize, ButtonType, ButtonVariant, HxButton } from '../../button/button';
import { HxDivider } from '../../divider/divider';
import { HxIconButton } from '../../icon-button/icon-button';
import { HxIcon } from '../../icon/icon';
import { HxPopoverDirective } from '../../popover/popover';
import { HxSwitch } from '../../switch/switch';
import { MenuButtonConfig, MenuItem, MenuItemSelectedEvent, MenuOpenEvent } from '../types/menu-config.types';

@Component({
  selector: 'hx-menu-button',
  standalone: true,
  imports: [CommonModule, HxIconButton, HxButton, HxIcon, HxDivider, HxPopoverDirective, HxSwitch],
  templateUrl: './menu-button.html',
  styleUrls: ['./menu-button.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'hx-menu-button',
    '[class.hx-menu-button--open]': 'isOpen()',
    '[class.hx-menu-button--disabled]': 'isDisabled()',
  },
})
export class HxMenuButtonComponent extends HxBaseControl {
  private elementRef = inject(ElementRef<HTMLElement>);

  /** Custom button template */
  readonly buttonTemplate = input<TemplateRef<unknown> | null>(null);

  /** Menu configuration */
  readonly config = input.required<MenuButtonConfig>();

  /** Button properties */
  readonly variant = input<ButtonVariant>('outline');
  readonly context = input<ButtonContext>('brand');
  readonly size = input<ButtonSize>('md');
  readonly fullWidth = input(false);
  readonly buttonType = input<ButtonType>('button');

  /** Events */
  readonly menuOpened = output<MenuOpenEvent>();
  readonly menuClosed = output<void>();
  readonly itemSelected = output<MenuItemSelectedEvent>();

  /** Template reference for menu panel */
  readonly menuPanelRef = viewChild<TemplateRef<unknown>>('menuPanel');

  /** Popover directive reference for programmatic control */
  readonly popoverRef = viewChild(HxPopoverDirective);

  // Internal state
  private readonly _isOpen = signal<boolean>(false);

  // Computed properties
  readonly isOpen = computed(() => this._isOpen());
  readonly menuItems = computed(() => this.config().items);
  readonly buttonLabel = computed(() => this.config().label || 'Menu');
  readonly isDisabled = computed(() => this.config().disabled || false);
  readonly isIconOnly = computed(() => {
    const cfg = this.config();
    return !!cfg.icon && !cfg.label;
  });

  readonly shouldScroll = computed(() => {
    const maxItems = this.config().maxItems || 6;
    return this.menuItems().length >= maxItems;
  });

  readonly hasCustomTemplate = computed(() => !!this.buttonTemplate());

  /** Handle menu item click */
  onMenuItemClick(item: MenuItem, index: number): void {
    if (item.disabled) {
      return;
    }

    const event: MenuItemSelectedEvent = {
      item,
      index,
    };

    this.onItemSelected(event);

    // Execute item action if provided
    if (item.action) {
      item.action();
    }
  }

  private getFontScaleData(item: MenuItem): Record<string, unknown> | null {
    return item.data && typeof item.data === 'object' ? (item.data as Record<string, unknown>) : null;
  }

  isFontScaleControl(item: MenuItem): boolean {
    return this.getFontScaleData(item)?.['fontScaleControl'] === true;
  }

  getFontScaleLabel(item: MenuItem): string {
    const label = this.getFontScaleData(item)?.['fontScaleLabel'];
    return typeof label === 'string' ? label : '100%';
  }

  isFontScaleResetDisabled(item: MenuItem): boolean {
    return this.getFontScaleData(item)?.['resetDisabled'] === true;
  }

  onFontScaleIncrease(item: MenuItem): void {
    if (!this.isItemDisabled(item)) item.action?.();
  }

  onFontScaleDecrease(item: MenuItem): void {
    if (!(item.secondaryDisabled ?? false)) item.secondaryAction?.();
  }

  onFontScaleReset(item: MenuItem): void {
    if (this.isFontScaleResetDisabled(item)) return;
    const action = this.getFontScaleData(item)?.['resetFontScale'];
    if (typeof action === 'function') (action as () => void)();
  }

  onSecondaryMenuItemClick(item: MenuItem, index: number): void {
    if (item.secondaryDisabled) {
      return;
    }

    item.secondaryAction?.();
    this.popoverRef()?.hide();
  }

  /** Close the menu after following an external link. */
  onExternalLinkClick(event: MouseEvent, item: MenuItem): void {
    if (this.isItemDisabled(item)) {
      event.preventDefault();
      return;
    }

    this.popoverRef()?.hide();
  }

  /** Handle a switch-style item toggling. Does not close the menu or fire `action`/`itemSelected`. */
  onSwitchToggle(item: MenuItem, checked: boolean): void {
    item.onCheckedChange?.(checked);
  }

  /** Check if item is disabled */
  isItemDisabled(item: MenuItem): boolean {
    return item.disabled || false;
  }

  /** Handle menu item selection */
  onItemSelected(event: MenuItemSelectedEvent): void {
    this.itemSelected.emit(event);
    this.popoverRef()?.hide();
  }

  /** Handle popover opened event */
  onPopoverOpened(): void {
    this._isOpen.set(true);

    const event: MenuOpenEvent = {
      trigger: this.elementRef.nativeElement,
    };
    this.menuOpened.emit(event);
  }

  /** Handle popover closed event */
  onPopoverClosed(): void {
    this._isOpen.set(false);

    this.menuClosed.emit();

    // Return focus to trigger button
    this.elementRef.nativeElement.querySelector('button')?.focus();
  }
}
